/** Converte "HH:MM" ou "HH:MM:SS" em minutos desde meia-noite. */
export function parseClockToMinutes(clock) {
  if (!clock) return null;
  const m = String(clock).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return hh * 60 + mm;
}

export function computeDurationMins(inicio, fim) {
  const dep = parseClockToMinutes(inicio);
  const arr = parseClockToMinutes(fim);
  if (dep == null || arr == null) return null;
  let delta = arr - dep;
  // Mesmo horário (ex. 09:00→09:00) ou chegada "antes" = viagem que vira o dia.
  if (delta <= 0) delta += 24 * 60;
  return delta;
}

export function formatDurationMins(mins) {
  if (mins == null || !Number.isFinite(mins) || mins < 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m}min`;
  return `${h}h${m > 0 ? ` ${String(m).padStart(2, "0")}min` : ""}`;
}

export function parseTripPrice(valor) {
  if (valor == null || valor === "") return null;
  const n = Number(String(valor).replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

export function formatTripPriceBRL(valor) {
  const n = parseTripPrice(valor);
  if (n == null) return valor ? `R$ ${valor}` : "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function sortViagens(list, mode = "time") {
  const arr = Array.isArray(list) ? [...list] : [];
  return sortTripEntries(
    arr.map((viagem, apiIndex) => ({ viagem, apiIndex })),
    mode
  ).map((e) => e.viagem);
}

/** Ordena para exibição mantendo o índice original da API (`opcao_viagem` = apiIndex + 1). */
export function sortTripEntries(entries, mode = "time") {
  const arr = Array.isArray(entries) ? [...entries] : [];
  if (mode === "price") {
    return arr.sort((a, b) => {
      const pa = parseTripPrice(a.viagem.valor) ?? Number.MAX_VALUE;
      const pb = parseTripPrice(b.viagem.valor) ?? Number.MAX_VALUE;
      if (pa !== pb) return pa - pb;
      return (
        (parseClockToMinutes(a.viagem.inicio) ?? 9999) - (parseClockToMinutes(b.viagem.inicio) ?? 9999)
      );
    });
  }
  return arr.sort((a, b) => {
    const da = parseClockToMinutes(a.viagem.inicio) ?? 9999;
    const db = parseClockToMinutes(b.viagem.inicio) ?? 9999;
    if (da !== db) return da - db;
    return (parseTripPrice(a.viagem.valor) ?? 0) - (parseTripPrice(b.viagem.valor) ?? 0);
  });
}

export function tripHasConexao(trip) {
  if (!trip || typeof trip !== "object") return false;
  if (trip.hasConexao === true) return true;
  const ds = trip.dados_especificos && typeof trip.dados_especificos === "object" ? trip.dados_especificos : {};
  if (ds.hasConexao === true) return true;
  if (Array.isArray(ds.Conexoes) && ds.Conexoes.length > 0) return true;
  if (Array.isArray(trip.trechos) && trip.trechos.length > 1) return true;
  if (Array.isArray(ds.trechos) && ds.trechos.length > 1) return true;
  if (ds.conexao || ds.localidadeConexaoId || ds.servicoConexao) return true;
  return false;
}

export function tripConnectionCity(trip) {
  if (!trip) return "";
  const ds = trip.dados_especificos || {};
  return String(trip.localidadeConexao || ds.localidadeConexao || ds.DescConexao || "").trim();
}

export function tripConnectionLegs(trip) {
  if (!trip) return [];
  const ds = trip.dados_especificos || {};
  if (Array.isArray(trip.trechos) && trip.trechos.length) return trip.trechos;
  if (Array.isArray(ds.trechos) && ds.trechos.length) return ds.trechos;
  return [];
}

export function tripItineraryLabel(trip, origemNome, destinoNome) {
  if (!trip) return "";
  const ds = trip.dados_especificos || {};
  if (trip.conexao_itinerario) return String(trip.conexao_itinerario);
  if (ds.conexao_itinerario) return String(ds.conexao_itinerario);
  const mid = tripConnectionCity(trip);
  const trechos = tripConnectionLegs(trip);
  const first = trechos[0]?.origemNome || origemNome;
  const last = (trechos.length ? trechos[trechos.length - 1]?.destinoNome : null) || destinoNome;
  const names = [first, mid, last].filter((n) => n && String(n).trim());
  const uniq = [];
  for (const n of names) {
    const s = String(n).trim();
    if (!uniq.length || uniq[uniq.length - 1].toLowerCase() !== s.toLowerCase()) uniq.push(s);
  }
  if (uniq.length >= 2) return uniq.join(" → ");
  if (ds.DescConexao) return String(ds.DescConexao);
  return "";
}

/** Praxio/Luna: ObrigaNascimento na partida ou em qualquer conexão. */
export function tripRequiresPassengerBirthDate(trip) {
  if (!trip || typeof trip !== "object") return false;
  const flagged = (node) => {
    if (!node || typeof node !== "object") return false;
    const direct = node.obrigaNascimento ?? node.ObrigaNascimento;
    if (direct === 1 || direct === "1" || direct === true) return true;
    const g = node.Gerenciamento || node.gerenciamento;
    const nested = g && (g.ObrigaNascimento ?? g.obrigaNascimento);
    return nested === 1 || nested === "1" || nested === true;
  };
  const ds = trip.dados_especificos || {};
  if (flagged(trip) || flagged(ds)) return true;
  const conexoes = ds.Conexoes || ds.conexoes || [];
  return Array.isArray(conexoes) && conexoes.some((c) => flagged(c));
}

export function tripListHighlights(viagens) {
  let bestPrice = null;
  let bestDur = null;
  for (const v of viagens || []) {
    const p = parseTripPrice(v.valor);
    if (p != null && (bestPrice == null || p < bestPrice)) bestPrice = p;
    const d = computeDurationMins(v.inicio, v.fim);
    if (d != null && (bestDur == null || d < bestDur)) bestDur = d;
  }
  return { bestPrice, bestDur };
}

export function normalizeTripClasse(raw) {
  if (!raw) return null;
  const lower = String(raw).trim().toLowerCase();
  if (!lower) return null;
  if (lower.includes("semi") && lower.includes("leito")) return "Semi leito";
  if (lower.includes("leito")) return "Leito";
  if (lower.includes("convencional")) return "Convencional";
  if (lower.includes("executivo")) return "Executivo";
  if (lower.includes("cama")) return "Cama";
  return String(raw).trim();
}

/** Maior = mais conforto (para ordenar categorias no mesmo horário). */
export function classeComfortRank(classe) {
  if (!classe) return 35;
  const l = classe.toLowerCase();
  if (l.includes("convencional")) return 10;
  if (l.includes("executivo")) return 22;
  if (l.includes("semi") && l.includes("leito")) return 30;
  if (l.includes("leito")) return 40;
  if (l.includes("cama")) return 50;
  return 32;
}

/** Texto para o atendente explicar a diferença de categoria no mesmo ônibus. */
export function classePitch(classe) {
  if (!classe) return null;
  const l = classe.toLowerCase();
  if (l.includes("semi") && l.includes("leito")) {
    return "Mesmo ônibus — poltrona com mais reclinação; costuma ser o melhor custo-benefício.";
  }
  if (l.includes("leito") && !l.includes("semi")) {
    return "Mesmo ônibus — mais reclínio e espaço; ideal para descansar na viagem.";
  }
  if (l.includes("cama")) {
    return "Conforto máximo para pernoite no mesmo itinerário.";
  }
  if (l.includes("executivo")) {
    return "Mais conforto no mesmo trecho e horário.";
  }
  if (l.includes("convencional")) {
    return "Opção econômica no mesmo horário.";
  }
  return null;
}

export function tripGroupKey(viagem) {
  const inicio = String(viagem?.inicio || "").trim();
  const fim = String(viagem?.fim || "").trim();
  const via = String(viagem?.viacao || "").trim().toLowerCase();
  const dur = computeDurationMins(inicio, fim);
  const conexao = tripHasConexao(viagem) ? tripItineraryLabel(viagem) || "cx" : "";
  return `${inicio}|${fim}|${via}|${dur ?? ""}|${conexao}`;
}

/**
 * Agrupa opções com mesmo horário/linha e deduplica por categoria de poltrona.
 * @returns {Array<{ key: string, items: Array<{ viagem, apiIndex }>, sameBus: boolean }>}
 */
export function buildTripDisplayGroups(entries) {
  const groups = [];
  const keyToIdx = new Map();

  for (const item of entries) {
    const key = tripGroupKey(item.viagem);
    let gi = keyToIdx.get(key);
    if (gi == null) {
      gi = groups.length;
      keyToIdx.set(key, gi);
      groups.push([]);
    }
    groups[gi].push(item);
  }

  return groups.map((items) => {
    const byClass = new Map();
    for (const it of items) {
      const c =
        normalizeTripClasse(it.viagem.classe) ??
        (String(it.viagem.classe ?? "").trim() || "_sem_classe");
      const price = parseTripPrice(it.viagem.valor) ?? Number.POSITIVE_INFINITY;
      const prev = byClass.get(c);
      const prevP = prev
        ? parseTripPrice(prev.viagem.valor) ?? Number.POSITIVE_INFINITY
        : Number.POSITIVE_INFINITY;
      if (!prev || price < prevP) byClass.set(c, it);
    }
    const deduped = Array.from(byClass.values());
    deduped.sort((a, b) => {
      const ra = classeComfortRank(normalizeTripClasse(a.viagem.classe));
      const rb = classeComfortRank(normalizeTripClasse(b.viagem.classe));
      if (ra !== rb) return ra - rb;
      return (
        (parseTripPrice(a.viagem.valor) ?? 0) - (parseTripPrice(b.viagem.valor) ?? 0)
      );
    });
    return {
      key: tripGroupKey(deduped[0]?.viagem),
      items: deduped,
      sameBus: deduped.length > 1,
    };
  });
}

export function listAvailableTripClasses(viagens) {
  const set = new Set();
  for (const v of viagens || []) {
    const c = normalizeTripClasse(v.classe);
    if (c) set.add(c);
  }
  return Array.from(set).sort((a, b) => classeComfortRank(a) - classeComfortRank(b));
}

export function filterEntriesByClasse(entries, classeFilter) {
  if (!classeFilter || classeFilter === "all") return entries;
  return entries.filter(
    (e) => normalizeTripClasse(e.viagem.classe) === classeFilter
  );
}

/** Período do dia pela hora de saída (para filtros rápidos do atendente). */
export function tripTimePeriod(inicio) {
  const mins = parseClockToMinutes(inicio);
  if (mins == null) return "unknown";
  if (mins < 360) return "madrugada";
  if (mins < 720) return "manha";
  if (mins < 1080) return "tarde";
  return "noite";
}

export function filterEntriesByPeriod(entries, period) {
  if (!period || period === "all") return entries;
  return entries.filter((e) => tripTimePeriod(e.viagem.inicio) === period);
}

/**
 * Capacidade de referência (poltronas) para estimar ocupação absoluta quando o parceiro
 * só informa poltronas livres (ônibus rodoviário típico: ~44–46 lugares).
 */
export const REFERENCE_BUS_CAPACITY = 46;

/**
 * Peso da rentabilidade da empresa na recomendação (0 = só o melhor p/ cliente, 1 = só empresa).
 * Padrão equilibrado: considera tarifa/ocupação sem ignorar o melhor para o passageiro.
 * Ajustável por operação (poderia vir da config do workspace no futuro).
 */
export const DEFAULT_COMPANY_WEIGHT = 0.5;

/** Janela (min) em torno do horário habitual do cliente para "encaixe" total. */
const PREFERRED_TIME_WINDOW_MIN = 240;

function clamp01(n) {
  return Math.max(0, Math.min(1, n));
}

/** Normaliza v no intervalo [min,max] → [0,1]; retorna 0.5 (neutro) se faixa degenerada. */
function normRange(v, min, max) {
  if (!Number.isFinite(v) || !(max > min)) return 0.5;
  return clamp01((v - min) / (max - min));
}

function weightedAvg(terms) {
  let sumW = 0;
  let acc = 0;
  for (const [w, v] of terms) {
    if (!Number.isFinite(v)) continue;
    sumW += w;
    acc += w * v;
  }
  return sumW > 0 ? acc / sumW : 0.5;
}

/** Deriva até 2 motivos legíveis (ou "best_value") a partir dos sub-scores. */
function deriveTripReasons({ priceNorm, durNorm, timeFit, loadScore, classNorm }) {
  const factors = [
    ["best_price", 1 - priceNorm],
    ["fastest", durNorm],
    ["high_occupancy", loadScore],
    ["premium", classNorm],
  ];
  if (timeFit != null) factors.push(["usual_time", timeFit]);
  const strong = factors
    .filter(([, v]) => Number.isFinite(v) && v >= 0.66)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([k]) => k);
  return strong.length ? strong : ["best_value"];
}

/**
 * Pontua as viagens equilibrando o melhor para o CLIENTE (preço, duração, horário habitual,
 * conforto) e a rentabilidade para a EMPRESA (yield/tarifa, categoria premium e, sobretudo,
 * taxa de ocupação/consolidação da demanda).
 *
 * Fundamento (revenue management de transporte): o inventário é perecível — assento vazio é
 * receita perdida —, então maximizar a ocupação (load factor) é o principal motor de
 * rentabilidade, combinado ao yield por assento. Consolidar a venda em partidas que já estão
 * enchendo aumenta o load factor final; tarifas/classes maiores elevam a receita por poltrona.
 *
 * @param {Array} viagens
 * @param {{ passengerCount?: number, preferredDepartureTime?: string|null, companyWeight?: number }} opts
 * @returns {{ ranked: Array<{apiIndex:number,score:number,customer:number,company:number,loadScore:number,reasons:string[]}>, best: object|null }}
 */
export function scoreTripsForSale(viagens, opts = {}) {
  const {
    passengerCount = 1,
    preferredDepartureTime = null,
    companyWeight = DEFAULT_COMPANY_WEIGHT,
  } = opts;
  const list = Array.isArray(viagens) ? viagens : [];
  const need = Math.max(1, Number(passengerCount) || 1);

  const candidates = [];
  for (let i = 0; i < list.length; i++) {
    const v = list[i];
    const seatsRaw = Number(v.poltronas_disponiveis);
    const seats = Number.isFinite(seatsRaw) ? seatsRaw : null;
    if (seats != null && seats < need) continue; // não cabe o grupo → inviável
    candidates.push({
      apiIndex: i,
      price: parseTripPrice(v.valor),
      dur: computeDurationMins(v.inicio, v.fim),
      seats,
      rank: classeComfortRank(normalizeTripClasse(v.classe)),
      dep: parseClockToMinutes(v.inicio),
    });
  }
  if (!candidates.length) return { ranked: [], best: null };

  const prices = candidates.map((c) => c.price).filter(Number.isFinite);
  const durs = candidates.map((c) => c.dur).filter(Number.isFinite);
  const ranks = candidates.map((c) => c.rank).filter(Number.isFinite);
  const avails = candidates.map((c) => c.seats).filter(Number.isFinite);
  const minP = prices.length ? Math.min(...prices) : 0;
  const maxP = prices.length ? Math.max(...prices) : 0;
  const minD = durs.length ? Math.min(...durs) : 0;
  const maxD = durs.length ? Math.max(...durs) : 0;
  const minR = ranks.length ? Math.min(...ranks) : 0;
  const maxR = ranks.length ? Math.max(...ranks) : 0;
  const minA = avails.length ? Math.min(...avails) : 0;
  const maxA = avails.length ? Math.max(...avails) : 0;
  const prefMin = preferredDepartureTime != null ? parseClockToMinutes(preferredDepartureTime) : null;
  const w = clamp01(Number(companyWeight));

  const ranked = candidates
    .map((c) => {
      const priceNorm = prices.length ? normRange(c.price, minP, maxP) : 0.5; // 0 barato .. 1 caro
      const durNorm = durs.length ? 1 - normRange(c.dur, minD, maxD) : 0.5; // 1 mais rápido
      const classNorm = maxR > minR ? normRange(c.rank, minR, maxR) : 0.5; // 1 mais premium

      // Ocupação/consolidação: combina sinal relativo (entre as opções do dia) e absoluto.
      const relFill = c.seats == null ? 0.5 : maxA > minA ? normRange(maxA - c.seats, 0, maxA - minA) : 0.5;
      const occAbs = c.seats == null ? 0.5 : clamp01(1 - c.seats / REFERENCE_BUS_CAPACITY);
      const loadScore = clamp01(0.6 * relFill + 0.4 * occAbs);

      const timeFit =
        prefMin != null && c.dep != null
          ? 1 - clamp01(Math.abs(c.dep - prefMin) / PREFERRED_TIME_WINDOW_MIN)
          : null;

      // Utilidade para o cliente (quanto maior, melhor para o passageiro).
      const customer = weightedAvg([
        [0.35, 1 - priceNorm], // mais barato
        [0.22, durNorm], // mais rápido
        [0.28, timeFit == null ? 0.5 : timeFit], // horário habitual
        [0.15, classNorm], // conforto
      ]);

      // Rentabilidade para a empresa (yield + ocupação).
      const company = weightedAvg([
        [0.38, priceNorm], // tarifa maior = mais receita por assento
        [0.20, classNorm], // categoria premium rende mais
        [0.42, loadScore], // taxa de ocupação / consolidação (motor do load factor)
      ]);

      // Penalidade leve por vender as últimas poltronas (risco operacional e p/ grupos).
      const tightPenalty = c.seats != null && c.seats < need + 2 ? 0.05 : 0;

      const score = (1 - w) * customer + w * company - tightPenalty;
      const reasons = deriveTripReasons({ priceNorm, durNorm, timeFit, loadScore, classNorm });
      return { apiIndex: c.apiIndex, score, customer, company, loadScore, reasons };
    })
    .sort((a, b) => b.score - a.score);

  return { ranked, best: ranked[0] || null };
}

/**
 * Sugestão automática para o atendente, equilibrando cliente × empresa (yield + ocupação).
 * @returns {{ apiIndex: number, reasons: string[] } | null}
 */
export function pickRecommendedTrip(viagens, passengerCount = 1) {
  const list = Array.isArray(viagens) ? viagens : [];
  if (!list.length) return null;
  if (list.length === 1) return { apiIndex: 0, reasons: ["single"] };
  const { best } = scoreTripsForSale(list, { passengerCount });
  return best ? { apiIndex: best.apiIndex, reasons: best.reasons } : { apiIndex: 0, reasons: [] };
}

/**
 * Inclui o horário habitual do cliente como forte sinal de utilidade, sem abrir mão do
 * equilíbrio cliente × empresa do `scoreTripsForSale`.
 */
export function pickTripByHistory(viagens, suggestions, passengerCount = 1) {
  const list = Array.isArray(viagens) ? viagens : [];
  if (!list.length) return null;
  if (list.length === 1) return { apiIndex: 0, reasons: ["single"] };
  const { best } = scoreTripsForSale(list, {
    passengerCount,
    preferredDepartureTime: suggestions?.preferredDepartureTime || null,
  });
  return best
    ? { apiIndex: best.apiIndex, reasons: best.reasons }
    : pickRecommendedTrip(list, passengerCount);
}
